/**
 * CodeMirror decorations for tokens and suggestions
 */

import {EditorState, StateField} from "@codemirror/state";
import {Decoration, DecorationSet, EditorView} from "@codemirror/view";
import {tokensField, setTokensEffect, suggestionsField, setSuggestionsEffect} from "../state/editor.state";
import {IngredientChipWidget} from "../widgets/chip.widget";

// ===== Token Decorations =====
function buildTokenDecorations(state: EditorState): DecorationSet {
  const tokens = state.field(tokensField);
  const ranges: any[] = [];

  for (const t of tokens) {
    // Skip empty tokens except draft tokens (they can be empty initially)
    if (t.to <= t.from && t.type !== "ingredient_draft") continue;

    if (t.type === "quantity") {
      ranges.push(Decoration.mark({class: "tok tok-quantity"}).range(t.from, t.to));
      continue;
    }

    if (t.type === "ingredient_draft") {
      // Only create decoration if the draft has content
      if (t.to > t.from) {
        ranges.push(Decoration.mark({class: "tok tok-ingredient-draft"}).range(t.from, t.to));
      }
      continue;
    }

    if (t.type === "ingredient") {
      const label = state.doc.sliceString(t.from, t.to);
      ranges.push(
        Decoration.replace({widget: new IngredientChipWidget(label), inclusive: true}).range(
          t.from,
          t.to
        )
      );
    }
  }

  return Decoration.set(ranges, true);
}

export const tokenDecorations = StateField.define<DecorationSet>({
  create(state) {
    return buildTokenDecorations(state);
  },
  update(deco, tr) {
    if (tr.docChanged || tr.effects.some((e) => e.is(setTokensEffect))) {
      return buildTokenDecorations(tr.state);
    }
    return deco.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

// ===== Atomic Ranges (for chips) =====
function buildAtomicRanges(state: EditorState): DecorationSet {
  const tokens = state.field(tokensField);
  const ranges: any[] = [];

  for (const t of tokens) {
    if (t.type !== "ingredient") continue;
    if (t.to > t.from) {
      ranges.push(Decoration.mark({class: "atomic"}).range(t.from, t.to));
    }
  }

  return Decoration.set(ranges, true);
}

export const tokenAtomicDeco = StateField.define<DecorationSet>({
  create(state) {
    return buildAtomicRanges(state);
  },
  update(deco, tr) {
    if (tr.docChanged || tr.effects.some((e) => e.is(setTokensEffect))) {
      return buildAtomicRanges(tr.state);
    }
    return deco.map(tr.changes);
  },
});

export const tokenAtomicExtension = EditorView.atomicRanges.of((view) =>
  view.state.field(tokenAtomicDeco)
);

// ===== Suggestion Decorations =====
function buildSuggestionDecorations(state: EditorState): DecorationSet {
  const suggestions = state.field(suggestionsField);
  const ranges: any[] = [];

  for (const s of suggestions) {
    if (s.to <= s.from) continue;

    ranges.push(
      Decoration.mark({
        class: "suggestion-mark",
        attributes: {
          "data-suggestion-type": s.suggestedType,
        },
      }).range(s.from, s.to)
    );
  }

  return Decoration.set(ranges, true);
}

export const suggestionDecorations = StateField.define<DecorationSet>({
  create(state) {
    return buildSuggestionDecorations(state);
  },
  update(deco, tr) {
    if (tr.docChanged || tr.effects.some((e) => e.is(setSuggestionsEffect))) {
      return buildSuggestionDecorations(tr.state);
    }
    return deco.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});
